import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import type { Property } from "@tycho/web-bindings/types";
import { IndeterminateProgress } from "@/web/components/primitives/indeterminate-progress";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type LoadingCompsProps = {
  /** Every targeted property whose comps are still being fetched. */
  properties: Property[];
};

const listFormatter = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

function LoadingCompsAction({ properties, onSkip }: LoadingCompsProps & SmbActionCoreProps) {
  const addresses = listFormatter.format(
    properties.map((p) => formatCanonicalAddressShort(p.address)),
  );
  return (
    <ActionCard category="negotiate">
      <ActionCard.Header>
        <ActionCard.Badge>Price</ActionCard.Badge>
      </ActionCard.Header>
      <ActionCard.Main>
        <ActionCard.Title>Kicking off pricing</ActionCard.Title>
        <ActionCard.Description>
          We're finding recent sales similar to <strong>{addresses}</strong>. You'll pick the most
          similar buildings, and we'll apply their price-per-square-foot to yours.
        </ActionCard.Description>
        <div className="mt-5 flex flex-col gap-3">
          <IndeterminateProgress
            className="bg-white/20"
            indicatorClassName="text-white"
            aria-label="Generating comparable properties"
          />
          <p className="text-xs opacity-70">This card will update as soon as it's ready.</p>
        </div>
      </ActionCard.Main>
      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
      </ActionCard.Footer>
    </ActionCard>
  );
}

export function createLoadingCompsAction(
  props: LoadingCompsProps & { id?: string },
): SmbActionDefinition {
  const { properties, id = "loading-comps" } = props;
  return {
    id,
    category: "negotiate",
    canSkip: true,
    render: (core) => <LoadingCompsAction properties={properties} {...core} />,
  };
}

// Single card across every targeted property: shows while ANY property's comps
// are still being fetched, listing all of them. Updates freely as targeted
// properties (and their comps-job statuses) change.
export const loadingCompsRule = CardRulesEngine.singletonRule({
  id: "loading-comps",
  priority: 80,
  when: (d) => d.properties.some((p) => CardRulesEngine.propertyCompsPending(d, p)),
  build: (d) =>
    createLoadingCompsAction({
      properties: d.properties.filter((p) => CardRulesEngine.propertyCompsPending(d, p)),
    }),
});
